<script setup lang="ts">
import { getElementUiObject, getHtmlFromRichText } from '@mop/cms/utils/utils';
import type { PrivacySettings } from '@/types/privacy';

export type SettingsEntry = {
  isOpen: boolean;
  checked: boolean;
};

export type CmsCookieBannerItem = {
  key: string;
  label: string;
  description?: string;
};

export type CmsCookieBanner = {
  headline: string;
  description?: string;
  items: CmsCookieBannerItem[];
  confirmationText?: string;
  footerText?: string;
  welcomeHeadline: string;
  welcomeDescription?: string;
  welcomeFooterText?: string;
};

defineOptions({
  name: 'MopCookiesOverlay',
});

const props = defineProps({
  showSettings: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['overlay-override']);

const { $storyblokLivePreview, $gtm } = useNuxtApp();
const { getCmsStory, cmsStoryModelRef } = useMopCms();
const { getPrivacySettings, setPrivacySettings, isCookieConsentRequired } = useMopPrivacy();
const overlay = useMopOverlay();
const settingsEntriesRef = ref<Record<string, SettingsEntry>>({});
const showSettingsRef = ref(props.showSettings);
const currentPrivacySettings = getPrivacySettings();

for (const key in currentPrivacySettings) {
  settingsEntriesRef.value[key] = {
    isOpen: false,
    checked: currentPrivacySettings[key as keyof PrivacySettings],
  };
}

onMounted(async () => {
  emit('overlay-override', { loading: true });
  await getCmsStory(constants.STORYBLOK.STORY_PATHS.COOKIE_BANNER);

  nextTick(() => {
    emit('overlay-override', { loading: false });
  });
});

$storyblokLivePreview.initStoryListener('CookieBanner', cmsStoryModelRef);

const cmsContentState = computed(() => {
  const cmsStoryModel = cmsStoryModelRef.value;
  if (!cmsStoryModel.isInitialized() || cmsStoryModel.hasError()) {
    return {};
  }
  const contentElements = cmsStoryModel.getContentElements();
  if (!contentElements || contentElements.length === 0) {
    return {};
  }
  const data: any = contentElements[0].getData();
  if (!data) {
    return {};
  }

  const headline = data.headline;
  const description = getHtmlFromRichText(data.description);
  const items: CmsCookieBannerItem[] = data.items.map((item: any) => {
    return getElementUiObject(item, {
      key: item.key,
      label: item.label,
      description: getHtmlFromRichText(item.description),
    } as CmsCookieBannerItem);
  });
  const confirmationText = getHtmlFromRichText(data.confirmationText);
  const footerText = getHtmlFromRichText(data.footerText);
  const welcomeHeadline = data.welcomeHeadline;
  const welcomeDescription = getHtmlFromRichText(data.welcomeDescription);
  const welcomeFooterText = getHtmlFromRichText(data.welcomeFooterText);

  const cookieBanner: CmsCookieBanner = getElementUiObject(data, {
    headline,
    description,
    items,
    confirmationText,
    footerText,
    welcomeHeadline,
    welcomeDescription,
    welcomeFooterText,
  });

  const keyItems: Record<string, CmsCookieBannerItem> = {};
  cookieBanner.items.forEach((item) => {
    keyItems[item.key] = item;
  });

  return {
    cookieBanner,
    keyItems,
  };
});

const cookieBannerHeadlineRef = computed(() =>
  showSettingsRef.value
    ? cmsContentState.value.cookieBanner?.headline
    : cmsContentState.value.cookieBanner?.welcomeHeadline,
);

const handleEntryToggle = (messageKey: string) => {
  settingsEntriesRef.value[messageKey].isOpen = !settingsEntriesRef.value[messageKey].isOpen;
};

const refreshSettingsEntries = () => {
  for (const key in settingsEntriesRef.value) {
    settingsEntriesRef.value[key].checked = currentPrivacySettings[key as keyof PrivacySettings];
  }
};

const reInitializeMarketingScripts = () => {
  if (overlay.activeOverlayRef.value) {
    overlay.activeOverlayRef.value.onClose = () => {};
  }
  setTimeout(() => {
    window.location.reload();
  }, 100);
};

const updatePrivacySettings = async (currentPrivacySettings: PrivacySettings) => {
  const isFirstCookieBannerVisit: boolean = isCookieConsentRequired();
  const hasCookiesChanged: boolean = setPrivacySettings(currentPrivacySettings);
  refreshSettingsEntries();
  if (!isFirstCookieBannerVisit && hasCookiesChanged) {
    reInitializeMarketingScripts();
  } else {
    $gtm.initGtmScript();
    await overlay.closeAll();
  }
};

const toggle = (selected = true, value?: boolean) => {
  for (const key in currentPrivacySettings) {
    currentPrivacySettings[key as keyof PrivacySettings] = selected
      ? Boolean(settingsEntriesRef.value[key].checked)
      : Boolean(value);
  }
  updatePrivacySettings(currentPrivacySettings);
};

const toggleAll = (value: boolean) => {
  toggle(false, value);
};

const handleSubmitSelectedSettings = () => {
  toggle();
};

const handleSubmitAllSettings = () => {
  toggleAll(true);
};

const handleFooterClick = (event: Event) => {
  const classList = (event.target as Element)?.classList;
  if (classList?.contains('storyblok-cookie-banner-settings')) {
    showSettingsRef.value = true;
    return;
  }
  if (classList?.contains('storyblok-cookie-banner-continue-with-mandatory')) {
    toggleAll(false);
  }
};
</script>

<template>
  <div v-if="cmsContentState.cookieBanner" v-storyblok-editable="cmsContentState.cookieBanner" class="cookie-settings">
    <div class="cookie-settings__header">
      {{ cookieBannerHeadlineRef }}
    </div>
    <template v-if="showSettingsRef">
      <div
        v-if="cmsContentState.cookieBanner.description"
        class="cookie-settings__description"
        v-html="cmsContentState.cookieBanner.description"
      />
      <UiForm class="cookie-settings__form" long>
        <div v-for="(entry, key) in settingsEntriesRef" :key="key" class="cookie-settings__form-entry">
          <div v-if="cmsContentState.keyItems?.[key]" class="cookie-settings__message-title-wrapper">
            <UiFormCheckbox
              v-model="entry.checked"
              :name="`${key}`"
              :label="cmsContentState.keyItems[key].label"
              bold-label
            />
            <div
              :class="[
                'cookie-settings__message-more',
                {
                  'cookie-settings__message-more--expanded': entry.isOpen,
                },
              ]"
              @click.prevent="handleEntryToggle(`${key}`)"
            >
              {{ $mopI18n.t('components.cookies.settings.show_details') }}
            </div>
          </div>
          <div
            v-if="entry.isOpen"
            class="cookie-settings__message-text"
            v-html="cmsContentState.keyItems[`${key}`].description"
          />
        </div>

        <div class="cookie-settings__confirmation-area">
          <div class="cookie-settings__confirmation-text" v-html="cmsContentState.cookieBanner.confirmationText" />
        </div>
      </UiForm>
      <div class="cookie-settings__actions">
        <button
          id="button-use-all-cookies"
          type="button"
          class="button button--primary"
          @click="handleSubmitAllSettings"
        >
          {{ $mopI18n.t('components.cookies.settings.submit_all') }}
        </button>
        <button
          id="button-use-selected-cookies"
          type="button"
          class="cookie-settings__submit-selected"
          @click="handleSubmitSelectedSettings"
        >
          {{ $mopI18n.t('components.cookies.settings.submit_selected') }}
        </button>
      </div>

      <div
        class="cookie-settings__confirmation-text"
        @click="handleFooterClick"
        v-html="cmsContentState.cookieBanner.footerText"
      />
    </template>
    <template v-else>
      <div class="cookie-settings__description" v-html="cmsContentState.cookieBanner.welcomeDescription" />
      <div class="cookie-settings__actions">
        <button
          id="button-use-all-cookies"
          type="button"
          class="button button--primary"
          @click="handleSubmitAllSettings"
        >
          {{ $mopI18n.t('components.cookies.settings.approve_all') }}
        </button>
      </div>

      <div class="cookie-settings__footer" @click="handleFooterClick">
        <div class="cookie-settings__description" v-html="cmsContentState.cookieBanner.welcomeFooterText" />
      </div>
    </template>
    <NuxtLink class="cookie-settings__imprint" :to="$mopI18n.localePath($urls.IMPRINT)" no-prefetch>
      {{ $mopI18n.t('components.cookies.settings.imprint') }}
    </NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
$cookie-settings-background-color: $white;

.cookie-settings {
  padding: $space50 calc(#{$space30} + #{columns(1)}) $space30;
  background-color: $cookie-settings-background-color;

  @include apply-upto(small) {
    padding-top: $space30;
  }

  :deep(.storyblok-cookie-banner-settings),
  :deep(.storyblok-cookie-banner-continue-with-mandatory),
  :deep(.rich-text a) {
    @include link-underlined(0);
  }
}

.cookie-settings__header {
  @include text-style(headline);

  @include apply-from(tiny) {
    margin-top: 0;
    margin-bottom: $space20;
  }
}

.cookie-settings__form-entry,
.cookie-settings__description {
  margin-bottom: $space20;
}

.cookie-settings__description,
.cookie-settings__description {
  &:deep(p) {
    @include text-style(small);
  }
}

.cookie-settings__confirmation-area {
  padding: $space5 0;

  @include apply-upto(small) {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    margin-right: -$space30;
    padding-right: $space30;
    background-color: $cookie-settings-background-color;
  }
}

.cookie-settings__actions {
  align-items: center;
  background-color: $cookie-settings-background-color;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: $space20;
  padding-top: $space20;
  position: sticky;
}

.cookie-settings__submit-selected {
  @include link-underlined($space10);

  margin-top: $space10;
}

.cookie-settings__confirmation-text,
.cookie-settings__confirmation-text {
  :deep(p) {
    @include text-style(small);
  }
}

.cookie-settings__message-text {
  display: block;
  margin: $space10 $space25 0 $space35;

  &:deep(p) {
    @include text-style(common);
  }
}

.cookie-settings__message-title-wrapper {
  width: 100%;
  display: flex;
  position: relative;
  justify-content: space-between;
  cursor: pointer;

  @include apply-upto(tiny) {
    flex-direction: column;
  }
}

.cookie-settings__message-more {
  @include text-style(small);

  white-space: nowrap;
  align-items: center;
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;

  @include apply-upto(tiny) {
    align-self: flex-start;
    margin-top: $space10;
    padding-left: $form-label-padding;
  }

  &::after {
    @include icon('arrow-select');

    content: '';
    margin-left: $space10;
    transition: transform ease 0.3s;
  }
}

.cookie-settings__message-more--expanded {
  &::after {
    transform: rotateZ(-135deg);
  }
}

.cookie-settings__form {
  width: 100%;
}

.cookie-settings__message-title {
  @include text-style(strong);

  position: relative;
  flex-grow: 1;
  padding-bottom: $space10;
  pointer-events: none;
}

.cookie-settings__imprint {
  @include link-underlined($space10);
}

.cookie-settings__underlined-link {
  @include link-underlined(0);
}
</style>
